import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { get } from "../../utils/api";
import styles from "./SectionB.module.css";

const SectionB = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [sectionBData, setSectionBData] = useState(null);

  useEffect(() => {
    if (!id) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await get(`/brsr/get/sections?id=${id}&BRSRSectionB=true`);
      setSectionBData(response?.data?.data?.BRSRSectionB || null);
    } catch (error) {
      console.log(error);
      setSectionBData(null);
    } finally {
      setLoading(false);
    }
  };

  const principles = useMemo(
    () => Array.from({ length: 9 }, (_, idx) => `principle${idx + 1}`),
    [],
  );
  const safeText = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") return fallback;
    return String(value);
  };
  const toYesNo = (value) => {
    if (value === true || value === "true" || value === "yes" || value === "Yes") {
      return "Yes";
    }
    if (value === false || value === "false" || value === "no" || value === "No") {
      return "No";
    }
    return "-";
  };

  const pm = sectionBData?.policyAndManagement || {};
  const gl = sectionBData?.governanceLeadership || {};

  const q11Rows = principles.map((key, index) => {
    const assessed = toYesNo(gl?.extenalAgencyPolicyEvaluation?.[key]);
    const agency = safeText(gl?.nameOfAgencyEvaluating?.[key], "");
    return {
      sno: `Principle ${index + 1}`,
      text:
        assessed === "Yes" && agency
          ? `${assessed}, ${agency}`
          : assessed,
    };
  });

  const q12Rows = principles.map((key, index) => ({
    sno: `P${index + 1}`,
    nonMaterial: toYesNo(gl?.nonMaterial?.[key]),
    notReady: toYesNo(gl?.notReady?.[key]),
    plannedNextYear: toYesNo(gl?.plannedNextYear?.[key]),
    resourceConstraints: toYesNo(gl?.resourceConstraints?.[key]),
    anyOther: safeText(gl?.reasonRemark?.[key], "-"),
  }));

  return (
    <div className={styles.sectionB}>
      <div className={`${styles.sectionTitle} section-heading-common common_text_style`}>
        SECTION B: MANAGEMENT AND PROCESS DISCLOSURES
      </div>
      <div className={styles.band}>I. POLICY AND MANAGEMENT PROCESSES</div>

      {loading ? (
        <div className={styles.meta}>Loading...</div>
      ) : !sectionBData ? (
        <div className={styles.meta}>No data found.</div>
      ) : (
        <>
          <table className={styles.table}>
            <colgroup>
              <col style={{ width: "15%" }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: "30%" }} />
            </colgroup>
            <thead>
              <tr>
                <th className={styles.disclosureQuestionCol}>Disclosure Questions</th>
                <th>
                  1. (a) Whether your entity&apos;s policy/policies cover each principle and
                  its core elements of the NGRBCs.
                </th>
                <th>1. (b) Has the policy been approved by the Board?</th>
                <th>1. (c) Web Link of the Policies, if available</th>
              </tr>
            </thead>
            <tbody>
              {principles.map((key, index) => (
                <tr key={key}>
                  <td
                    className={`${styles.labelCell} ${styles.disclosureQuestionCol}`}
                  >{`P${index + 1}`}</td>
                  <td>{toYesNo(pm?.policyCoveringPrinciples?.[key])}</td>
                  <td>{toYesNo(pm?.boardApprovedPolicies?.[key])}</td>
                  <td className={styles.linkCell}>{safeText(pm?.webLinksForPolicies?.[key], "-")}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.question}>
            2. Whether the entity has translated the policy into procedures.
          </div>
          <div className={styles.answer}>{safeText(pm?.policiesTranslatedToProcedures, "-")}</div>

          <div className={styles.question}>
            3. Do the enlisted policies extend to your value chain partners? (Yes/No)
          </div>
          <div className={styles.point3Grid}>
            {principles.map((key, index) => (
              <div key={key} className={styles.point3Item}>
                {`Principle ${index + 1}: `}
                <strong>{toYesNo(pm?.policiesExtendToValueChainPartners?.[key])}</strong>
              </div>
            ))}
          </div>

          <div className={styles.question}>
            4. Name of the National & International codes/certificates/labels/standards
            (e.g. Forest Stewardship Council, Fairtrade, Rainforest Alliance, Trustea)
            standards (e.g. SA 8000, OHSAS, ISO, BIS) adopted by your entity and mapped
            to each principle.
          </div>
          <div className={styles.answer}>{safeText(pm?.codesCertificationsLabelsStandards)}</div>

          <div className={styles.question}>
            5. Specific commitments, goals and targets set by the entity with defined
            timelines, if any.
          </div>
          <div className={styles.answer}>{safeText(pm?.commitmentsGoalsTargets)}</div>

          <div className={styles.question}>
            6. Performance of the entity against the specific commitments, goals & targets
            along with reasons in case the same are not met.
          </div>
          <div className={styles.answer}>{safeText(pm?.performanceAgainstGoals)}</div>

          <div className={styles.pageBreak} />
          <div className={styles.band}>II. GOVERNANCE, LEADERSHIP AND OVERSIGHT</div>

          <div className={styles.question}>
           7. Statement of Director responsible for the business responsibility report, highlighting ESG related challenges, targets and
            achievements (listed entity has flexibility regarding the placement of this disclosure).
          </div>
          <div className={styles.answer}>{safeText(gl?.directorStatementESG)}</div>

          <div className={styles.question}>
            8. Details of the highest authority responsible for implementation & oversight
            of Business Responsibility Policy/Policies.
          </div>
          <div className={styles.answer}>{safeText(gl?.oversightBusinessResponsibility)}</div>

          <div className={styles.question}>
            9. Does the entity have a specified committee of Board/Director responsible for
            decision making on sustainability related issues? If Yes, provide details.
          </div>
          <div className={styles.answer}>
            {toYesNo(gl?.independentAssessment?.status)}
            {gl?.independentAssessment?.status
              ? ` - ${safeText(gl?.independentAssessment?.remark)}`
              : ""}
          </div>

          <div className={styles.question}>10. Details of Review of NGRBCs by the Company:</div>
          <p className={styles.reviewNote}>
            Indicate whether review was undertaken by Director / Committee of
            the Board/ Any other Committee
          </p>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: "80px" }}>Subject for Review</th>
                <th>Performance against above policies and follow up action</th>
                <th>
                  Compliance with statutory requirements of relevance to the
                  principles, and, rectification of any non-compliances
                </th>
                <th>1. (c) Web Link of the Policies, if available</th>
              </tr>
            </thead>
            <tbody>
              {principles.map((key, index) => (
                <tr key={key}>
                  <td
                    className={`${styles.labelCell} ${styles.subjectReviewCell}`}
                  >{`P${index + 1}`}</td>
                  <td>{toYesNo(gl?.performanceReviewDirector?.[key])}</td>
                  <td>{toYesNo(gl?.performanceCompliance?.[key])}</td>
                  <td className={styles.linkCell}>
                    {safeText(pm?.webLinksForPolicies?.[key], "-")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.subText}>
            Frequency (Annually/ Half yearly/ Quarterly/ Any other - please specify)
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: "80px" }}>Subject for Review</th>
                <th>Performance against above policies and follow up action</th>
                <th>
                  Compliance with statutory requirements of relevance to the
                  principles, and rectification of any non-compliances
                </th>
              </tr>
            </thead>
            <tbody>
              {principles.map((key, index) => (
                <tr key={key}>
                  <td
                    className={`${styles.labelCell} ${styles.subjectReviewCell}`}
                  >{`P${index + 1}`}</td>
                  <td>{safeText(gl?.frequencyReviewDirector?.[key], "-")}</td>
                  <td>{safeText(gl?.frequencyCompliance?.[key], "-")}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.question}>
            11. Has the entity carried out independent assessment/ evaluation of the
            working of its policies by an external agency? (Yes/No). If yes, provide name
            of the agency.
          </div>
          <div className={styles.point11Wrap}>
            {q11Rows.map((row, index) => (
              <div key={row.sno} className={styles.point11Item}>
                {row.sno}:{" "}
                <span
                  className={styles.point11Answer}
                  style={{ fontWeight: 500 }}
                >
                  {row.text}
                </span>
                {index % 3 !== 2 ? <span className={styles.point11Spacer} /> : null}
              </div>
            ))}
          </div>

          <div className={styles.question}>
            12. If answer to question (1) above is &quot;No&quot; i.e. not all Principles are
            covered by a policy, reasons to be stated:
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: "80px" }}>Disclosure Questions</th>
                <th>The entity does not
                consider the Principles
                material to its business.
                </th>
                <th>The entity is not at a stage to formulate and implement policies.
</th>
                <th>Planned in the next financial year</th>
                <th>
                  The entity does not have the financial or/ human and technical resources
                  available for the task (Yes/No)
                </th>
                <th>Any other reason (please specify)</th>
              </tr>
            </thead>
            <tbody>
              {q12Rows.map((row) => (
                <tr key={row.sno}>
                  <td>{row.sno}</td>
                  <td>{row.nonMaterial}</td>
                  <td>{row.notReady}</td>
                  <td>{row.plannedNextYear}</td>
                  <td>{row.resourceConstraints}</td>
                  <td>{row.anyOther}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default SectionB;

